/**
 * The per-screen subsection selection of the setup wizard, plus the single
 * rule for turning an untrusted persisted value back into a usable selection.
 */

import type { Option } from '../types';

export type ConnectorBindingSection = 'connectors' | 'bom' | 'components';
export type BoltConnectionSection = 'core' | 'supporting';
export type ProcessPropertiesSection = 'processConditions' | 'projectProperties';
export type AdditionalCategoriesSection = 'valve' | 'supports' | 'instrument';

export interface WorkflowSectionsState {
  connectorBinding: ConnectorBindingSection;
  boltConnection: BoltConnectionSection;
  processProperties: ProcessPropertiesSection;
  additionalCategories: AdditionalCategoriesSection;
}

export const initialSectionsState: WorkflowSectionsState = {
  connectorBinding: 'connectors',
  boltConnection: 'core',
  processProperties: 'processConditions',
  additionalCategories: 'valve',
};

const connectorSections = new Set<ConnectorBindingSection>(['connectors', 'bom', 'components']);
const boltSections = new Set<BoltConnectionSection>(['core', 'supporting']);
const processSections = new Set<ProcessPropertiesSection>([
  'processConditions',
  'projectProperties',
]);
const additionalCategorySections = new Set<AdditionalCategoriesSection>([
  'valve',
  'supports',
  'instrument',
]);

export const sanitizeSectionsState = (
  sections: unknown,
  selectedOption: Option
): WorkflowSectionsState => {
  if (!sections || typeof sections !== 'object') {
    return initialSectionsState;
  }

  const persistedSections = sections as Partial<WorkflowSectionsState>;

  return {
    connectorBinding: connectorSections.has(
      persistedSections.connectorBinding as ConnectorBindingSection
    )
      ? (persistedSections.connectorBinding as ConnectorBindingSection)
      : initialSectionsState.connectorBinding,
    boltConnection: boltSections.has(persistedSections.boltConnection as BoltConnectionSection)
      ? (persistedSections.boltConnection as BoltConnectionSection)
      : initialSectionsState.boltConnection,
    processProperties: processSections.has(
      persistedSections.processProperties as ProcessPropertiesSection
    )
      ? (persistedSections.processProperties as ProcessPropertiesSection)
      : initialSectionsState.processProperties,
    additionalCategories:
      selectedOption === 'Iso Drawings Generation' &&
      additionalCategorySections.has(
        persistedSections.additionalCategories as AdditionalCategoriesSection
      )
        ? (persistedSections.additionalCategories as AdditionalCategoriesSection)
        : initialSectionsState.additionalCategories,
  };
};
